<template>
  <section>
    <b-modal
      id="modal-send-mail"
      ref="modal-send-mail"
      data-cy="send-mail-modal"
      :data-view-ready="!loading"
      :modal-class="['has-confirmation-modal', minimized ? 'minimized-block' : 'full-block', minus ? 'minus-block' : '']"
      no-close-on-backdrop
      no-close-on-esc>
      <template #modal-header>
        <h5
          v-if="minus"
          class="modal-title">
          <span v-if="subject !== ''">
            {{ subject.length > 20 ? subject.substring(0, 20) + '...' : subject }}
          </span>
          <span v-else>
            {{ $t('message.generic.email') }}
          </span>
        </h5>
        <h5
          v-else
          class="modal-title">
          {{ $t('message.marketingMessages.sendMail') }}
          <span
            v-if="sendMailData.secondaryTitle && sendMailData.toAllTo"
            class="secondary-title ml-2">{{ $t('message.marketingMessages.secondOneMailTitle') }}</span>
          <span
            v-else-if="sendMailData.secondaryTitle"
            class="secondary-title ml-2">{{
              $t('message.marketingMessages.secondTitle')
            }}</span>
        </h5>
        <div class="d-flex">
          <coozzy-button
            design="transparent"
            @click="minusEvent">
            <coozzy-minus-icon name="minus" />
          </coozzy-button>
          <coozzy-button
            v-if="!minimized"
            design="transparent"
            @click="changeMinimized">
            <coozzy-minimize-icon name="minimize" />
          </coozzy-button>
          <coozzy-button
            v-if="minimized"
            class="ml-2"
            design="transparent"
            size="small"
            @click="changeMinimized">
            <coozzy-full-screen-icon name="full-screen" />
          </coozzy-button>
          <b-button
            class="close ml-2"
            size="sm"
            @click="discardChanges">
            x
          </b-button>
        </div>
      </template>
      <div
        v-if="loading"
        class="row">
        <div class="col text-center">
          <coozzy-spinner class="m-3" />
        </div>
      </div>
      <div
        v-else
        class="row">
        <div
          v-if="mobileView"
          class="col-sm-12 col-md-12 mb-2">
          <coozzy-form-checkbox
            v-model="confidentialEmail"
            :disabled="!isMailboxMainUser"
            :initial="confidentialEmail"
            name="confidentialEmail">
            {{ $t('message.settings.confidentialEmails.confidential') }}
          </coozzy-form-checkbox>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.marketingMessages.mail.mailbox') }}*</label>
          <coozzy-form-select
            v-model="from"
            data-cy="send-mail-mailbox-select"
            :state="$v.from.$dirty && $v.from.$error ? false : null">
            <option
              v-for="mailbox in getMailboxesForUserId(userId)"
              :key="mailbox.id"
              :value="mailbox">
              {{ mailbox.outboundEmail }}
            </option>
          </coozzy-form-select>
        </div>
        <div
          v-if="sendMailData.onlyCurrentContact"
          class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.marketingMessages.mail.to') }}*</label>
          <coozzy-search-multiselect
            v-model="to"
            data-cy="send-mail-to-multiselect"
            :check-valide="$v.to.$dirty && $v.to.$error ? 'is-invalid' : ''"
            :clear-on-select="false"
            :close-on-select="false"
            :custom-label="nameWithEmail"
            :is-email="true"
            :multiple="true"
            :options="toList"
            :placeholder="''"
            :preselect-first="false"
            :preserve-search="true"
            :taggable="true"
            :with-address="false"
            label="email"
            track-by="id" />
        </div>
        <div
          v-else
          class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.marketingMessages.mail.to') }}*</label>
          <contact-search-select
            id="to-multiselect"
            v-model="to"
            :filters="contactSearchFilters"
            :items-per-page="15"
            :transform-hits="toOptions"
            :check-valide="$v.to.$dirty && $v.to.$error ? 'is-invalid' : ''"
            :clear-on-select="false"
            :close-on-select="false"
            :custom-label="nameWithEmail"
            :email-with-name="true"
            :internal-search="false"
            :is-email="true"
            :multiple="true"
            :placeholder="''"
            :preselect-first="false"
            :preserve-search="true"
            :taggable="true"
            :track-by="'email'"
            :with-address="false"
            @search-change="searchQuery = $event" />
        </div>
        <div
          v-if="showRedInfoBar"
          class="col-sm-12 col-md-12 mb-2">
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="red-error-bar"
            v-html="$t('message.marketingMessages.mail.dedicatedErrorMessage')" />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div
          v-if="!sendMailData.useBcc || sendMailData.toAllTo"
          class="col-sm-12 col-md-12 mb-2">
          <label>CC</label>
          <contact-search-select
            v-model="cc"
            :filters="contactSearchFilters"
            :items-per-page="15"
            :transform-hits="ccOptions"
            :check-valide="$v.cc.$dirty && $v.cc.$error ? 'is-invalid' : ''"
            :clear-on-select="false"
            :close-on-select="false"
            :custom-label="nameWithEmail"
            :email-with-name="true"
            :internal-search="false"
            :is-email="true"
            :multiple="true"
            :placeholder="''"
            :preselect-first="false"
            :preserve-search="true"
            :taggable="true"
            :track-by="'email'"
            :with-address="false"
            @search-change="searchQuery = $event" />
        </div>
        <div
          v-if="(!sendMailData.useBcc || sendMailData.toAllTo) && !sendMailData.hasContext"
          class="col-sm-12 col-md-12 mb-2">
          <label>BCC</label>
          <contact-search-select
            v-model="bcc"
            :filters="contactSearchFilters"
            :items-per-page="15"
            :transform-hits="bccOptions"
            :check-valide="$v.bcc.$dirty && $v.bcc.$error ? 'is-invalid' : ''"
            :clear-on-select="false"
            :close-on-select="false"
            :custom-label="nameWithEmail"
            :email-with-name="true"
            :internal-search="false"
            :is-email="true"
            :multiple="true"
            :placeholder="''"
            :preselect-first="false"
            :preserve-search="true"
            :taggable="true"
            :track-by="'email'"
            :with-address="false"
            @search-change="searchQuery = $event" />
          <a @click="changeBcc">
            {{ $t('message.marketingMessages.mail.removeBCC') }}
          </a>
        </div>
        <div
          v-else-if="!sendMailData.useBcc || sendMailData.toAllTo"
          class="col-sm-12 col-md-12 mb-2">
          <a @click="changeBcc">
            {{ $t('message.marketingMessages.mail.insertBCC') }}
          </a>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.settings.template.title') }}</label>
          <coozzy-form-select
            v-model="templateId"
            data-cy="send-mail-template-select">
            <option :value="null" />
            <option
              v-for="template in sortedTemplate"
              :key="template.id"
              :value="template">
              {{ template.name }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <coozzy-form-input
            v-model="subject"
            data-cy="send-mail-subject-input"
            :name="$t('message.marketingMessages.mail.subject') + '*'"
            :state="$v.subject.$dirty && $v.subject.$error ? false : null" />
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label for="description">{{ $t('message.marketingMessages.mail.body') }}</label>
          <coozzy-alert
            v-if="(renderWithObjectId === '' || contactsWithObject.length > 1) && (bodyHtml.search('#anrede#') !== -1 || subject.search('#anrede#') !== -1 || bodyHtml.search('#obj') !== -1 || subject.search('#obj') !== -1)"
            variant="blue">
            {{ $t('message.marketingMessages.mail.placeholderMessage') }}
          </coozzy-alert>
          <editor
            v-if="showEditor"
            :key="keyRefresh"
            ref="editor-tiny"
            v-model="bodyHtml"
            :api-key="editorApi"
            :init="editorConfig"
            @onInit="onEditorInit" />
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <section class="mt-2 mb-2">
            <div
              v-b-toggle="'collapse-placeholder'"
              class="collapse-title">
              <label class="cursor-pointer width-only-content">
                {{ $t('message.generic.placeholder') }}
              </label>
            </div>
            <b-collapse
              :id="'collapse-placeholder'"
              :visible="false"
              class="col">
              <p class="mb-0">
                <span>#anrede# <br></span>
                <span v-if="showAllPlacholders">
                  #obj.str# <br>
                  #obj.nr# <br>
                  #obj.plz# <br>
                  #obj.ort# <br>
                  #obj.zimmer# <br>
                  #obj.etage#
                </span>
              </p>
            </b-collapse>
          </section>
        </div>
        <div class="col-sm-12 col-md-12 mb-2">
          <label>{{ $t('message.calendar.event.attachment') }}</label>
          <coozzy-form-file-input
            id="file"
            ref="coozzy-file-input"
            :disabled="uploading"
            accept="text/plain, text/xml,
                   image/*, video/*,
                   application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/pdf, application/postscript, application/zip,
                   .doc, .docx, .xls, .xlsx, .ppt, .pptx, .pdf, .avx, .avw, .avz"
            multiple
            @change="addFile"
            @dragover.prevent />
          <div
            v-if="uploading"
            class="row">
            <div class="col text-center">
              <coozzy-spinner class="m-3" />
            </div>
          </div>
          <div
            v-else
            class="row">
            <div
              v-for="media in mediasList"
              :key="media.id"
              class="media-block col-12">
              <div class="file-block p-1 mt-2 mb-0">
                <a
                  :href="media.url"
                  target="_blank">
                  {{ media.filename }}
                </a>
                <coozzy-button
                  class="delete-icon"
                  design="transparent"
                  @click="deleteMedia(media.id)">
                  <coozzy-delete-icon />
                </coozzy-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <div class="w-100 float-right">
          <coozzy-button
            :disabled="loading"
            class="float-right ml-1 ml-md-3"
            design="green"
            data-cy="send-mail-send-button"
            @click="checkAttachment(false)">
            {{ $t('message.marketingMessages.sendMail') }}
          </coozzy-button>
          <coozzy-button
            v-if="!sendMailData.secondaryTitle"
            :disabled="loading"
            class="float-right ml-1 ml-md-3"
            design="green"
            data-cy="send-mail-save-draft-button"
            @click="checkAttachment(true)">
            {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
          </coozzy-button>
          <div
            v-if="!mobileView"
            class="d-flex float-right confidential-checkbox">
            <coozzy-form-checkbox
              v-model="confidentialEmail"
              :disabled="!isMailboxMainUser"
              :initial="confidentialEmail"
              class="ml-3 mr-1 pr-0"
              name="confidentialEmail" />
            <span class="align-self-center">
              {{ $t('message.settings.confidentialEmails.confidential') }}
            </span>
          </div>
          <coozzy-button
            :disabled="loading"
            design="default"
            data-cy="send-mail-cancel-button"
            @click="discardChanges">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <list-contact-without-email-modal
      v-if="sendMailData.contactsWithoutEmail.length > 0"
      :all-contacts="allContacts"
      :contacts="sendMailData.contactsWithoutEmail" />
    <b-modal
      :id="'confirmationDiscardEmailModal'"
      ref="confirmationDiscardEmailModal"
      :title="$t('message.marketingMessages.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop
      size="md">
      <div class="col p-0">
        <p>{{ $t('message.marketingMessages.confirmModal.confirmationDiscardChanges') }}</p>
      </div>
      <div class="col p-0">
        <coozzy-button
          class="mb-0 border"
          design="transparent"
          size="small"
          @click="$bvModal.hide('confirmationDiscardEmailModal')">
          {{ $t('message.generic.selectField.no') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          @click="closeAll()">
          {{ $t('message.generic.selectField.yes') }}
        </coozzy-button>
        <coozzy-button
          v-if="!sendMailData.secondaryTitle"
          class="float-right mb-0 mr-2"
          design="green"
          size="small"
          @click="checkAttachment(true)">
          {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      id="missing-attachment-warning-modal"
      ref="missingAttachmentWarningModal"
      :title="$t('message.marketingMessages.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop
      size="md">
      <div class="col p-0">
        <p>{{ $t('message.marketingMessages.missingAttachmentModal.text') }}</p>
      </div>
      <div class="col p-0">
        <coozzy-button
          class="mb-0 border"
          design="transparent"
          size="small"
          @click="addAttachmentFromWarning">
          {{ $t('message.marketingMessages.missingAttachmentModal.addAttachment') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          @click="sendAnyway">
          {{ $t('message.marketingMessages.missingAttachmentModal.sendAnyway') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>
<script>
import { required } from 'vuelidate/lib/validators'
import MessageApi from '@/misc/apis/MessageApi'
import ContactApi from '@/misc/apis/ContactApi'
import MediaApi from '@/misc/apis/MediaApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import { buildPrintableOutboundEmailHtml, renderEmailPdfBlob } from '@/misc/emailPdf'
import ContactSearchSelect from '@/framework/components/search/ContactSearchSelect.vue'
import { contact } from '@/mixins/contact'
import ListContactWithoutEmailModal from '@/contact/components/ListContactWithoutEmailModal'
import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { user } from '@/mixins/user'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzySearchMultiselect from '@/framework/components/multiselect/CoozzySearchMultiselect'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import { responsivity } from '@/mixins/responsivity'
import Editor from '@tinymce/tinymce-vue'
import { media } from '@/mixins/media'
import CoozzyAlert from '@/framework/components/alert/CoozzyAlert.vue'
import { eventBus, mail } from '@/mixins/mail'
import CoozzyMinimizeIcon from '@/framework/components/icons/CoozzyMinimizeIcon.vue'
import CoozzyFullScreenIcon from '@/framework/components/icons/CoozzyFullScreenIcon.vue'
import CoozzyMinusIcon from '@/framework/components/icons/CoozzyMinusIcon.vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import * as Sentry from '@sentry/vue'
import {
  exceedsOutgoingEmailAttachmentLimit,
  outgoingEmailAttachmentLimitI18nKey
} from '@/misc/constants'
import { isValidEmailFormat, recipientEmailForSend } from '@/misc/utils/contactEmail'

/** MIME types allowed as mail attachments (aligned with coozzy-form-file-input accept; excludes image/*). */
const SEND_MAIL_ATTACHMENT_MIMES = new Set([
  'text/plain',
  'text/xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf',
  'application/postscript',
  'application/zip'
])

/** Extensions when type is missing (aligned with attachment file input). */
const SEND_MAIL_ATTACHMENT_EXTS = new Set([
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'avx', 'avw', 'avz', 'txt', 'xml'
])

function isValidEmail(value) {
  if (!value || value < 0) {
    return true
  }
  let status = false
  value.forEach(element => {
    if (!recipientEmailForSend(element) && !status) {
      status = true
    }
  })

  return !status
}

export default {
  name: 'SendMailModal',
  components: {
    CoozzyMinusIcon,
    CoozzyFullScreenIcon,
    CoozzyMinimizeIcon,
    CoozzyFormCheckbox,
    CoozzyAlert,
    ListContactWithoutEmailModal,
    CoozzyDeleteIcon,
    CoozzyButton,
    CoozzyFormFileInput,
    CoozzySearchMultiselect,
    CoozzyFormSelect,
    CoozzySpinner,
    CoozzyFormInput,
    ContactSearchSelect,
    editor: Editor
  },
  mixins: [user, responsivity, media, mail, contact],
  data() {
    return {
      minus: false,
      minimized: false,
      discradDraft: false,
      isDraft: false,
      draftId: null,
      templateId: null,
      bodyHtml: '',
      subject: '',
      from: null,
      to: [],
      attachment: [],
      loading: false,
      keyRefresh: 0,
      replyToMailId: '',
      medias: [],
      mediasList: [],
      isFirst: true,
      cc: [],
      bcc: [],
      uploading: false,
      lastMailboxId: null,
      hasDedicated: false,
      showRedInfoBar: false,
      isReply: false,
      isForward: false,
      confirmDiscardShown: false,
      missingAttachmentWarningShown: false,
      showEditor: false,
      allContacts: [],
      showAllPlacholders: false,
      confidentialEmail: false,
      totalFilesSize: 0,
      searchQuery: '',
      modalShownHandler: null,
      modalHiddenHandler: null,
      editorBodyDropHandler: null,
      editorDropBody: null
    }
  },
  computed: {
    ...mapState('message', ['sendMailData', 'templates', 'confidentialMailDomain']),
    ...mapGetters('message', ['getMailboxesForUserId', 'getSignatureForUserId', 'getSentMails', 'getMailboxEntries', 'getShowAllPlaceholders']),
    fontSetting() {
      return this.$store.getters['user/getFontSettings']
    },
    contentStyle() {
      const defaultFontFamily = 'Lato, sans-serif'
      const fontFamily = this.fontSetting?.family || defaultFontFamily
      const fontSize = `${this.fontSetting?.size || 10}pt`
      const fontColor = this.getConfiguredFontColor()
      return `body { color: ${fontColor} !important; font-family: ${fontFamily}; font-size: ${fontSize};} p {margin: 0}`
    },
    fontsizeFormats() {
      let sizeOptions = []
      for (let i = 4; i <= 36; i++) {
        sizeOptions.push(`${i}pt`)
      }
      return sizeOptions.join(' ')
    },
    fontFormats() {
      const defaultFont = 'Lato=Lato, sans-serif;'
      const userFont = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}=${this.fontSetting.family};` : defaultFont
      return `${userFont}`
    },
    signatureForCurrentUser() {
      let signature = ''
      if (this.getSignatureForUserId(this.userId, this.from?.signatureId) !== '') {
        signature = this.getSignatureForUserId(this.userId, this.from?.signatureId)
      } else if (this.getSignatureForUserId(this.from?.mainUserId, this.from?.signatureId)) {
        signature = this.getSignatureForUserId(this.from?.mainUserId, this.from?.signatureId)
      } else {
        signature = this.getSignatureForUserId(null, this.from?.signatureId)
      }

      return this.buildSignatureSection(signature)
    },
    sortedTemplate() {
      return this.templates.filter(x => x.type === 'TEMPLATE_TYPE_EMAIL').slice().sort((a, b) => {
        if (a.name > b.name) {
          return 1
        }
        if (b.name > a.name) {
          return -1
        }
        return 0
      })
    },
    contactSearchFilters() {
      return { isOwner: false, active: true, hasEmail: true, isOnlyInterestedParty: false }
    },
    toList() {
      let contactPersons = []
      for (const contact of this.sendMailData.contacts) {
        if (contact.contactPersons && contact.contactPersons.length > 0) {
          contactPersons = contactPersons.concat(contact.contactPersons)
        }
      }

      return [...new Set(this.sendMailData.contacts.concat(contactPersons).filter(hit => {
        return hit.email !== ''
      }))]
    },
    renderWithObjectId() {
      return this.sendMailData.renderWithObjectId
    },
    contactsWithObject() {
      return this.sendMailData.contactsWithObject
    },
    editorApi() {
      return 'gpl'
    },
    editorConfig() {
      return {
        toolbar: 'undo redo | bold italic underline forecolor | fontsize | numlist bullist link table | fullscreen print',
        plugins: 'fullscreen lists code image table link autolink',
        height: '370',
        menubar: '',
        language: this.currentLanguage,
        elementpath: false,
        automatic_uploads: true,
        license_key: 'gpl',
        content_style: this.contentStyle,
        font_formats: this.fontFormats,
        branding: false,
        remove_trailing_brs: false,
        browser_spellcheck: true,
        table_toolbar: '',
        font_size_formats: this.fontsizeFormats,
        invalid_styles: {
          '*': 'font-family'
        },
        convert_urls: false,
        link_title: false,
        link_default_protocol: 'https',
        default_link_target: '_blank',
        body_id: 'content',
        images_upload_handler: async (blobInfo, progress) => {
          try {
            // Convert blobInfo to File
            const blob = blobInfo.blob()
            const file = new File([blob], blobInfo.filename(), { type: blob.type })

            // Use existing uploadMedia function from media mixin, with real progress
            const response = await this.uploadMedia(
              [file],
              this.accountId,
              'false',
              null,
              (event) => {
                if (event && event.lengthComputable) {
                  const percent = Math.round((event.loaded * 100) / event.total)
                  progress(percent)
                }
              }
            )

            // Check if upload failed (uploadMedia returns null on error)
            if (!response || !response.length || !response[0]) {
              // eslint-disable-next-line no-throw-literal
              throw { message: this.$t('message.savingErrors.file'), remove: true }
            }

            // Ensure progress is set to 100% at the end
            progress(100)

            // Extract URL from the first media object in the response
            const media = response[0]
            if (!media || !media.url) {
              // eslint-disable-next-line no-throw-literal
              throw { message: this.$t('message.savingErrors.file'), remove: true }
            }

            // Return the image URL
            return media.url
          } catch (error) {
            // If error is already in the correct format, rethrow it
            // TinyMCE requires errors in format { message, remove }, not Error objects
            if (error && typeof error === 'object' && error.message && error.remove !== undefined) {
              // eslint-disable-next-line no-throw-literal
              throw error
            }
            // Otherwise, wrap it in the expected format
            // eslint-disable-next-line no-throw-literal
            throw { message: error?.message || this.$t('message.savingErrors.file'), remove: true }
          }
        },
        setup: (editor) => {
          const removeStyleProperty = (element, propertyName) => {
            if (!element || !element.style) return
            element.style.removeProperty(propertyName)
            const currentStyle = element.getAttribute('style')
            if (currentStyle !== null && currentStyle.trim() === '') {
              element.removeAttribute('style')
            }
          }

          const removeWordSpecificStyles = (element) => {
            if (!element || !element.getAttribute) return
            const styleAttr = element.getAttribute('style')
            if (!styleAttr) return

            const normalizedStyle = styleAttr
              .split(';')
              .map(chunk => chunk.trim())
              .filter(chunk => chunk && !/^mso-/i.test(chunk))
              .join('; ')

            if (normalizedStyle === '') {
              element.removeAttribute('style')
              return
            }

            element.setAttribute('style', normalizedStyle)
          }

          const removeFontFormatting = (rootElement) => {
            if (!rootElement) return

            const allElements = [rootElement, ...rootElement.querySelectorAll('*')]
            allElements.forEach(el => {
              removeStyleProperty(el, 'font-family')
              removeStyleProperty(el, 'font')
              removeWordSpecificStyles(el)
              el.removeAttribute?.('face')
              el.removeAttribute?.('size')
            })

            const fontElements = rootElement.querySelectorAll('font')
            fontElements.forEach(fontElement => {
              const parent = fontElement.parentNode
              if (!parent) return
              while (fontElement.firstChild) {
                parent.insertBefore(fontElement.firstChild, fontElement)
              }
              parent.removeChild(fontElement)
            })
          }

          let signatureSnapshot = null

          const restoreSignature = () => {
            if (!signatureSnapshot) return
            const body = editor.getBody()
            const signature = body?.querySelector('#signature')
            if (signature) signature.outerHTML = signatureSnapshot
            else if (body) body.insertAdjacentHTML('beforeend', signatureSnapshot)
            signatureSnapshot = null
          }

          editor.on('init', () => {
            const body = editor.getBody()
            const fontFamily = this.fontSetting?.family || 'Lato, sans-serif'
            body.style.fontFamily = fontFamily

            // Capture phase: run before TinyMCE rejects non-image drops ("Dropped file type is not supported.")
            this.bindEditorBodyDropListener(body)
          })

          // Track if a paste operation is in progress
          let isPasting = false

          // paste_preprocess: Marks empty paragraphs with data-empty-para="true" and uses &nbsp; as a placeholder
          editor.on('paste_preprocess', (plugin, args) => {
            isPasting = true
            const signature = editor.getBody()?.querySelector('#signature')
            if (signature && !signature.contains(editor.selection.getNode())) {
              signatureSnapshot = signature.outerHTML
            }

            // Create a temporary div to parse the HTML
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = args.content

            // Strip font-related and Word-specific formatting from pasted content
            removeFontFormatting(tempDiv)

            // Find all empty paragraphs and mark them
            const paragraphs = tempDiv.querySelectorAll('p')
            paragraphs.forEach(p => {
              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              // Check if paragraph is empty (no text, only whitespace, or only <br>)
              if (textContent === '' || innerHTML === '' || innerHTML === '<br>' || innerHTML === '<br/>') {
                p.setAttribute('data-empty-para', 'true')
                p.innerHTML = '&nbsp;'
              }
            })

            args.content = tempDiv.innerHTML
          })

          // paste_postprocess: Restores marked empty paragraphs by setting them to <br>
          editor.on('paste_postprocess', (plugin, args) => {
            const body = editor.getBody()

            // Find all paragraphs marked as empty and restore them
            const emptyParagraphs = body.querySelectorAll('p[data-empty-para="true"]')
            emptyParagraphs.forEach(p => {
              p.removeAttribute('data-empty-para')
              p.innerHTML = '<br>'
            })

            // Also check for any empty paragraphs that might have been missed
            const allParagraphs = body.querySelectorAll('p')
            allParagraphs.forEach(p => {
              // Skip paragraphs inside the signature section
              if (p.closest('#signature')) return
              // Skip paragraphs that contain images or other media elements
              if (p.querySelector('img, video, iframe, object, embed, svg, canvas')) return

              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              if ((textContent === '' || innerHTML === '' || innerHTML === '&nbsp;') && !p.hasAttribute('data-empty-para')) {
                p.innerHTML = '<br>'
              }
            })

            restoreSignature()
            isPasting = false
          })

          // Converts plain text into paragraph/list HTML while preserving bullet and numbered lists
          const convertPlainTextToHtml = (text) => {
            const escapeHtml = value => value
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;')

            const linkifyAndEscape = (value) => {
              const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi
              const parts = value.split(urlRegex)

              return parts.map((part, index) => {
                // Odd indexes are URL matches because split keeps capture groups
                if (index % 2 === 1) {
                  let urlPart = part
                  let trailingPunctuation = ''

                  while (/[.,;:!?]$/.test(urlPart)) {
                    trailingPunctuation = urlPart.slice(-1) + trailingPunctuation
                    urlPart = urlPart.slice(0, -1)
                  }

                  if (!urlPart) {
                    return escapeHtml(part)
                  }

                  const href = /^https?:\/\//i.test(urlPart) ? urlPart : `https://${urlPart}`
                  return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(urlPart)}</a>${escapeHtml(trailingPunctuation)}`
                }

                return escapeHtml(part)
              }).join('')
            }

            const lines = text.split(/\r?\n/)
            const blocks = []
            let currentListType = null
            let currentListItems = []

            const flushCurrentList = () => {
              if (!currentListType || currentListItems.length === 0) {
                currentListType = null
                currentListItems = []
                return
              }

              const listItemsHtml = currentListItems.map(item => `<li>${item}</li>`).join('')
              blocks.push(`<${currentListType}>${listItemsHtml}</${currentListType}>`)
              currentListType = null
              currentListItems = []
            }

            for (const line of lines) {
              const trimmedLine = line.trim()

              if (trimmedLine === '') {
                flushCurrentList()
                blocks.push('<p><br></p>')
                continue
              }

              const unorderedMatch = line.match(/^\s*([*•\-+])\s+(.+)$/)
              const orderedMatch = line.match(/^\s*(\d+|[a-zA-Z])[.)]\s+(.+)$/)

              if (unorderedMatch || orderedMatch) {
                const listType = unorderedMatch ? 'ul' : 'ol'
                const content = linkifyAndEscape((unorderedMatch || orderedMatch)[2].trim())

                if (currentListType !== listType) {
                  flushCurrentList()
                  currentListType = listType
                }

                currentListItems.push(content)
                continue
              }

              if (currentListType && currentListItems.length > 0 && /^\s+/.test(line)) {
                const continuation = linkifyAndEscape(trimmedLine)
                currentListItems[currentListItems.length - 1] += `<br>${continuation}`
                continue
              }

              flushCurrentList()
              blocks.push(`<p>${linkifyAndEscape(line)}</p>`)
            }

            flushCurrentList()
            return blocks.join('')
          }

          // paste event handler: Intercepts plain text pastes and converts line breaks to HTML paragraphs/lists
          editor.on('paste', (e) => {
            const clipboardData = e.clipboardData || window.clipboardData

            if (!clipboardData) return

            // Check if it's plain text (no HTML)
            const htmlData = clipboardData.getData('text/html')
            const textData = clipboardData.getData('text/plain')

            // If there's HTML data, let TinyMCE handle it normally
            if (htmlData && htmlData.trim() !== '') {
              return
            }

            // If it's plain text, convert line breaks to paragraphs/lists
            if (textData) {
              e.preventDefault()
              const html = convertPlainTextToHtml(textData)
              editor.insertContent(html)
            }
          })

          // PastePostProcess event: Ensures empty paragraphs have <br> tags after paste
          editor.on('PastePostProcess', () => {
            const body = editor.getBody()

            // Find all empty paragraphs and ensure they have <br> tags
            const paragraphs = body.querySelectorAll('p')
            paragraphs.forEach(p => {
              // Skip paragraphs inside the signature section
              if (p.closest('#signature')) return
              // Skip paragraphs that contain images or other media elements
              if (p.querySelector('img, video, iframe, object, embed, svg, canvas')) return

              const textContent = p.textContent.trim()
              const innerHTML = p.innerHTML.trim()

              // If paragraph is empty or only contains whitespace/&nbsp;
              if (textContent === '' || innerHTML === '' || innerHTML === '&nbsp;' || innerHTML === '<br>' || innerHTML === '<br/>') {
                p.innerHTML = '<br>'
              }
            })

            restoreSignature()
          })

          // SetContent event: Final safeguard that tracks paste operations and ensures empty paragraphs are preserved
          editor.on('SetContent', () => {
            // Only process if this was triggered by a paste operation
            if (!isPasting) return

            setTimeout(() => {
              const body = editor.getBody()
              const paragraphs = body.querySelectorAll('p')

              paragraphs.forEach(p => {
                // Skip paragraphs inside the signature section
                if (p.closest('#signature')) return
                // Skip paragraphs that contain images or other media elements
                if (p.querySelector('img, video, iframe, object, embed, svg, canvas')) return

                const textContent = p.textContent.trim()
                const innerHTML = p.innerHTML.trim()

                // Ensure empty paragraphs have <br> tags
                if (textContent === '' || innerHTML === '' || innerHTML === '&nbsp;') {
                  p.innerHTML = '<br>'
                }
              })
              restoreSignature()
            }, 0)
          })
        }
      }
    },
    isMailboxMainUser() {
      return this.from?.mainUserId === this.userId
    },
    outgoingEmailMailboxType() {
      return this.from?.mailboxType || null
    }
  },
  watch: {
    minimized(newVal) {
      if (newVal) {
        this.minus = false
      }
      this.syncSendMailModalDockMode()
    },
    minus(newVal) {
      if (newVal) {
        this.minimized = false
      }
      this.syncSendMailModalDockMode()
    },
    getSentMails: function () {
      if (this.lastMailboxId) {
        localStorage.setItem('lastMailboxId', this.lastMailboxId)
      }
    },
    getMailboxEntries: function (newVal) {
      if (newVal.length > 0 && newVal[0].mailboxes) {
        // Extract unique signatureIds with their corresponding ownerIds
        const signatureMap = new Map()
        newVal[0].mailboxes.forEach(mailbox => {
          if (mailbox.signatureId && !signatureMap.has(mailbox.signatureId)) {
            signatureMap.set(mailbox.signatureId, mailbox.ownerId)
          }
        })

        // Load signatures for each unique signatureId
        signatureMap.forEach((ownerId, signatureId) => {
          this.loadSignatureForUserId({
            accountId: ownerId,
            userId: this.userId,
            signatureId: signatureId
          })
        })
      }
    },
    getShowAllPlaceholders: function (newVal) {
      this.showAllPlacholders = newVal
    },
    signatureForCurrentUser: function (newSignature) {
      // get the part of html that has <section id="signature" in bodyHtml
      const signatureMatch = this.bodyHtml.match(/<section id="signature"[\s\S]*?<\/section>/i)
      const signature = signatureMatch ? signatureMatch[0] : ''
      if (!this.sendMailData.isDraft) {
        this.bodyHtml = this.bodyHtml.replaceAll(signature, newSignature)
      }
    },
    from: {
      handler: function (val, oldVal) {
        if (val?.outboundEmail) {
          // this.loadSignatureForUserId({ accountId: this.accountId, userId: this.userId, signatureId: val.signatureId })
          MessageApi.hasDomainDedicatedIp(val.outboundEmail)
            .then(async response => {
              this.hasDedicated = response.hasDedicated
              this.changeColorsToTags()
            }).catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
          })
        }
        this.checkConfidentialMail()
        if (oldVal && val && exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, val.mailboxType) &&
          !exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, oldVal.mailboxType)) {
          this.showAttachmentSizeError()
        }
      }
    },
    to: {
      handler: function (value) {
        if (value.length > 0) {
          this.changeColorsToTags()
          this.checkConfidentialMail()
        }
      }
    },
    cc: {
      handler: function () {
        this.checkConfidentialMail()
      }
    },
    bcc: {
      handler: function () {
        this.checkConfidentialMail()
      }
    },
    templateId: function (val) {
      if (val !== null) {
        this.loadTemplate()
      } else {
        this.bodyHtml = '<div><br /></div>' + this.signatureForCurrentUser +
          (this.sendMailData.emailToReply && this.sendMailData.emailToReply.bodyHtml ? '\n\n' + this.sendMailData.emailToReply.bodyHtml : '')
        this.subject = ''
        this.medias = []
        this.mediasList = []
      }
    }
  },
  mounted: function () {
    this.loadMailData()
  },
  beforeDestroy() {
    this.unbindEditorBodyDropListener()
    window.removeEventListener('keydown', this.checkInput)
    if (this.modalShownHandler) {
      this.$root.$off('bv::modal::shown', this.modalShownHandler)
    }
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
  },
  methods: {
    ...mapMutations('message', ['resetSendMailData', 'addSentMail', 'setShowAllPlaceholders']),
    ...mapActions('message', ['loadTemplateList', 'loadMailboxesForUserId', 'loadSignatureForUserId', 'loadConfidentialMailDomain']),
    ...mapActions('user', ['setSendMailModalDockMode']),
    ensureEditorBodyDropHandler() {
      if (this.editorBodyDropHandler) {
        return
      }
      this.editorBodyDropHandler = (ev) => {
        const editor = this.$refs['editor-tiny']?.editor
        const dt = ev.dataTransfer
        if (!dt || !dt.files || dt.files.length === 0) {
          return
        }
        const files = Array.from(dt.files)
        const attachmentCandidates = files.filter(f => this.isSendMailAttachmentFile(f))
        if (attachmentCandidates.length === 0) {
          return
        }
        ev.preventDefault()
        ev.stopPropagation()
        ev.stopImmediatePropagation()

        const imageFiles = files.filter(f => f.type && f.type.startsWith('image/'))
        const listFilesLimitExceeded = this.stageFilesForAttachment(attachmentCandidates)

        const run = async () => {
          if (this.attachment.length > 0) {
            await this.sendFile()
          }
          this.notifyMaxAttachmentSizeExceeded(listFilesLimitExceeded.length > 0)
          if (editor) {
            for (const imgFile of imageFiles) {
              await this.insertUploadedImageIntoEditor(editor, imgFile)
            }
          }
        }
        run().catch((err) => {
          console.log(err)
        })
      }
    },
    bindEditorBodyDropListener(body) {
      if (!body) {
        return
      }
      this.ensureEditorBodyDropHandler()
      this.unbindEditorBodyDropListener()
      this.editorDropBody = body
      body.addEventListener('drop', this.editorBodyDropHandler, true)
    },
    unbindEditorBodyDropListener() {
      if (this.editorDropBody && this.editorBodyDropHandler) {
        this.editorDropBody.removeEventListener('drop', this.editorBodyDropHandler, true)
      }
      this.editorDropBody = null
    },
    getConfiguredFontColor() {
      const color = this.fontSetting?.color
      if (typeof color === 'string' && color.trim() !== '') {
        return color
      }
      if (color && typeof color === 'object' && typeof color.red === 'number') {
        const alpha = color.alpha == null ? 1 : color.alpha
        return `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`
      }
      return '#495057'
    },
    getSignatureStyle() {
      const defaultFontFamily = 'Lato, sans-serif'
      const fontFamily = this.fontSetting?.family || defaultFontFamily
      const fontSize = `${this.fontSetting?.size || 10}pt`
      return `font-family: ${fontFamily}; color: ${this.getConfiguredFontColor()}; font-size: ${fontSize};`
    },
    buildSignatureSection(signature = '') {
      return `<section id="signature" contenteditable="true" style="${this.getSignatureStyle()}">${signature}</section>`
    },
    async loadMailData() {
      if (!this.modalShownHandler) {
        this.modalShownHandler = async (bvEvent, modalId) => {
          if (modalId === 'modal-send-mail') {
            this.bodyHtml = '<div><br /></div>' + this.signatureForCurrentUser
            this.$store.dispatch('user/setSendMailModalOpen', true)
            this.$v.$reset()
            window.addEventListener('keydown', this.checkInput)
            if (this.getMailboxesForUserId(this.userId).length === 0) {
              this.$nextTick(function () {
                Vue.toasted.show(this.$t('message.settings.mailbox.configMailboxToast'), { type: 'error' })
                this.hide()
              })
            }
            if (this.sendMailData?.isDraft) {
              this.isDraft = this.sendMailData.isDraft
              this.draftId = this.sendMailData.id
            }
            if (this.sendMailData.attachments?.length > 0) {
              this.medias = this.sendMailData.attachments.map(media => media.id)
              this.mediasList = this.sendMailData.attachments
              this.syncAttachmentTotalBytes()
            }
            if (this.sendMailData.bodyHtml) {
              this.bodyHtml = this.sendMailData.bodyHtml
            }
            if (this.sendMailData?.attachmentMediaIds?.length > 0) {
              const promises = this.sendMailData.attachmentMediaIds.map(media => MediaApi.getMediaByID(media))

              const response = await Promise.all(promises)
              if (!this.$store.getters['user/isSendMailModalOpen']) {
                return
              }
              this.mediasList = response.map(obj => obj.media)
              this.medias = response.map(obj => obj.media.id)
              this.syncAttachmentTotalBytes()
            }
            if (this.sendMailData.bcc.length > 0) {
              this.sendMailData.hasContext = false
              this.bcc = this.sendMailData.bcc
            }
            this.prepareSenderAndReceiver()
            if (!this.sendMailData?.isDraft) {
              if (this.sendMailData.emailToReply) {
                this.isReply = true
                this.isForward = false
                await this.prepareMailWithReply()
              }
              // Dismissed while prepare/fetch was in flight — do not finish compose setup (COOZ-15720)
              if (!this.$store.getters['user/isSendMailModalOpen']) {
                return
              }
              this.allContacts = JSON.parse(JSON.stringify(this.sendMailData.preselectedContact)) || []
              if (this.sendMailData.emailToForward) {
                this.isReply = false
                this.isForward = true
                await this.prepareMailToForward()
              }
            } else {
              this.allContacts = JSON.parse(JSON.stringify(this.sendMailData.preselectedContact)) || []
              this.restoreDraftReplyContext()
            }
            if (!this.$store.getters['user/isSendMailModalOpen']) {
              return
            }

            this.$nextTick(function () {
              if (!this.$store.getters['user/isSendMailModalOpen']) {
                return
              }
              this.$store.dispatch('user/setChangedModal', false).then().catch(e => console.log(e))
              this.keyRefresh += 1
              this.showEditor = true
            })
            this.$nextTick(function () {
              // prevent Bootstrap from hijacking TinyMCE modal focus when clicking in input of link's modal (link feature)
              document.getElementById('modal-send-mail___BV_modal_content_')?.removeAttribute('tabindex')
            })
            // Reset the discard flag after data is loaded so future closes properly reset the store
            this.discradDraft = false
            this.$nextTick(() => {
              this.syncSendMailModalDockMode()
            })
          }
          if (modalId === 'confirmationDiscardEmailModal') {
            this.confirmDiscardShown = true
          }
          if (modalId === 'missing-attachment-warning-modal') {
            this.missingAttachmentWarningShown = true
          }

          setTimeout(() => {
            if (this.$refs['editor-tiny']) {
              this.updateEditorContentStyle()
            }
          }, 500)
        }
        this.$root.$on('bv::modal::shown', this.modalShownHandler)
      }
      if (!this.modalHiddenHandler) {
        this.modalHiddenHandler = (bvEvent, modalId) => {
          this.$emit('restore')
          if (modalId === 'modal-send-mail') {
            this.$emit('restore')
            this.$store.dispatch('user/setSendMailModalOpen', false)
            this.totalFilesSize = 0
            this.minus = false
            this.minimized = false
            this.isForward = false
            this.isReply = false
            this.isDraft = false
            this.draftId = null
            window.removeEventListener('keydown', this.checkInput)
            this.unbindEditorBodyDropListener()
            // One-shot: discard-and-reopen sets discradDraft so this hide skips store reset;
            // always clear afterwards so a later hide (or aborted shown) cannot leave it stuck (COOZ-15720)
            if (!this.discradDraft) {
              this.resetSendMailData(false)
            }
            this.discradDraft = false
            this.replyToMailId = ''
            this.from = null
            this.to = []
            this.cc = []
            this.bcc = []
            this.subject = ''
            this.bodyHtml = ''
            this.medias = []
            this.mediasList = []
            this.attachment = []
            if (this.signatureForCurrentUser) {
              this.bodyHtml = '<div><br /></div>' + this.signatureForCurrentUser
            }
            this.templateId = null
            this.setShowAllPlaceholders(false)
            this.$nextTick(function () {
              this.showEditor = false
              document.getElementsByClassName('body')[0].style.overflow = 'auto'
            })
          }
          if (modalId === 'listContactsWithoutEmailModal') {
            this.isForward = false
            this.isReply = false
            this.resetSendMailData()
            this.replyToMailId = ''
            this.from = null
            this.to = []
            this.cc = []
            this.bcc = []
            this.subject = ''
            this.bodyHtml = ''
            this.medias = []
            this.mediasList = []
            this.attachment = []
            if (this.signatureForCurrentUser) {
              this.bodyHtml = '<div><br /></div>' + this.signatureForCurrentUser
            }
            this.templateId = null
          }
          if (modalId === 'confirmationDiscardEmailModal') {
            this.confirmDiscardShown = false
          }
          if (modalId === 'missing-attachment-warning-modal') {
            this.missingAttachmentWarningShown = false
          }
        }
        this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
      }
      this.loadTemplateList(this.accountId)
      if (this.getMailboxesForUserId(this.userId).length > 0) {
        this.loadConfidentialMailDomain(this.accountId)
      }
      this.loadSignatureForUserId({
        accountId: this.accountId,
        userId: this.userId,
        signatureId: this.from?.signatureId
      })
      if (this.signatureForCurrentUser && !this.sendMailData?.isDraft) {
        this.bodyHtml = '<div><br /></div>' + this.signatureForCurrentUser
      }
    },
    onEditorInit() {
      if (this.$refs['editor-tiny']) {
        const editor = this.$refs['editor-tiny'].editor
        this.$nextTick(() => {
          if (editor) {
            editor.focus()
          }
        })
      }
    },
    updateEditorContentStyle() {
      if (this.$refs['editor-tiny']) {
        const editor = this.$refs['editor-tiny'].editor
        if (editor.getBody()) {
          editor.getBody().style.fontFamily = this.fontSetting?.family || 'Lato'
          editor.getBody().style.fontSize = (this.fontSetting?.size || 10) + 'pt'
          editor.getBody().style.color = this.getConfiguredFontColor()
        }
      }
    },
    checkConfidentialMail() {
      if (this.sendMailData?.emailToReply?.confidential) {
        this.confidentialEmail = true
        return
      }
      if (this.from) {
        this.confidentialEmail = this.from.confidential
        return
      }
      if (this.confidentialMailDomain.length > 0) {
        const allRecipients = [...this.to, ...this.cc, ...this.bcc]
        this.confidentialEmail = allRecipients.some(item =>
          this.confidentialMailDomain.some(domain =>
            (item.email && item.email.includes(domain)) ||
            (item.workEmail?.use === true && typeof item.workEmail === 'string' && item.workEmail.includes(domain))
          )
        )
      }
    },
    discardChanges(e) {
      if (this.sendMailData?.saveAsDraft) {
        this.checkAttachment(true)
      } else {
        e.preventDefault()
        this.$refs.confirmationDiscardEmailModal?.show()
      }
    },
    checkInput(event) {
      if (event.key === 'Escape') {
        if (this.missingAttachmentWarningShown === true) {
          this.$bvModal.hide('missing-attachment-warning-modal')
        } else if (this.confirmDiscardShown === true) {
          this.$refs.confirmationDiscardEmailModal?.hide()
        } else {
          this.discardChanges(event)
        }
      }
    },
    closeAll() {
      this.$bvModal.hide('confirmationDiscardEmailModal')
      this.$v.$reset()
      this.$bvModal.hide('modal-send-mail')
    },
    nameWithEmail(item) {
      return item.name + ' ' + '<' + item.email + '>'
    },
    changeBcc() {
      this.sendMailData.hasContext = !this.sendMailData.hasContext
    },
    prepareSenderAndReceiver() {
      const mailboxes = this.getMailboxesForUserId(this.userId)
      if (mailboxes.length === 0) return
      const normalizeEmail = email => (email || '').trim().toLowerCase()

      // Set 'from' mailbox
      const lastMailboxId = localStorage.getItem('lastMailboxId')
      const mainMailBox = mailboxes.find(mailbox => mailbox.mainUserId === this.userId)
      const storedMailbox = mailboxes.find(mailbox => mailbox.id === lastMailboxId && mailbox.active)

      this.from = mainMailBox || storedMailbox || null

      // Handle reply case
      if (this.sendMailData.emailToReply) {
        const emailReplyFrom = this.sendMailData.emailToReply.from
        const originalTo = this.sendMailData.emailToReply.to || []
        const originalCc = this.sendMailData.emailToReply.cc || []
        const replyToEmail = this.sendMailData?.emailToReply?.replyTo?.trim?.() || this.sendMailData?.emailToReply?.replyTo || ''
        const isReplyOne = !!this.sendMailData.isReplyOne
        const normalizeEmail = (email) => email?.trim?.().toLowerCase?.() || ''
        const senderEmail = normalizeEmail(emailReplyFrom?.email)
        const originalToEmails = originalTo.map(recipient => normalizeEmail(recipient?.email))
        const originalCcEmails = originalCc.map(recipient => normalizeEmail(recipient?.email))

        const senderMailbox = mailboxes.find(mailbox => normalizeEmail(mailbox.outboundEmail) === senderEmail)
        const emailToReply = this.sendMailData.emailToReply
        const mailboxFromMessage = mailboxes.find(mailbox => mailbox.id === emailToReply.mailboxId)
        // Only treat as outbound when the message belongs to the sender's mailbox (mailboxId matches).
        // Inbound mail from another account mailbox must not use senderMailbox (COOZ-15566).
        const isOutboundReplyContext = !!(mailboxFromMessage && senderMailbox && mailboxFromMessage.id === senderMailbox.id)
        const receivedMailbox = mailboxes.find(mailbox => {
          const outboundEmail = normalizeEmail(mailbox.outboundEmail)
          return originalToEmails.includes(outboundEmail) || originalCcEmails.includes(outboundEmail)
        })
        // Always reply from the user's own mailbox when available (COOZ-15566).
        const userMailbox = mainMailBox || mailboxFromMessage || receivedMailbox

        if (userMailbox) {
          this.from = userMailbox
        }

        if (isReplyOne) {
          if (replyToEmail) {
            this.to = [{ email: replyToEmail, name: '' }]
          } else {
            const replyCandidates = []
            const currentMailboxEmail = normalizeEmail(userMailbox?.outboundEmail)
            const isOutboundReply = isOutboundReplyContext

            if (isOutboundReply) {
              replyCandidates.push(...originalTo)
              if (replyCandidates.length === 0) {
                const fallbackCc = originalCc.find(recipient =>
                  normalizeEmail(recipient?.email) !== currentMailboxEmail &&
                  (recipient.active ?? true)
                )
                if (fallbackCc) {
                  replyCandidates.push(fallbackCc)
                }
              }
            } else if (emailReplyFrom?.email) {
              replyCandidates.push(emailReplyFrom)
            }

            this.to = replyCandidates
              .filter(recipient =>
                recipient?.email &&
                normalizeEmail(recipient.email) !== currentMailboxEmail &&
                (recipient.active ?? true)
              )
              .map(recipient => this.resolveContactByEmail(recipient.email) || recipient)
              .reduce((acc, recipient) => {
                if (!acc.some(existing => normalizeEmail(existing.email) === normalizeEmail(recipient.email))) {
                  acc.push(recipient)
                }
                return acc
              }, [])
          }
        } else {
          const currentMailboxEmail = normalizeEmail(userMailbox?.outboundEmail)
          const replyAllTo = this.sendMailData.contacts?.length > 0 ? this.sendMailData.contacts : originalTo
          this.to = replyAllTo
            .filter(recipient =>
              recipient?.email &&
              normalizeEmail(recipient.email) !== currentMailboxEmail &&
              (recipient.active ?? true)
            )
            .map(recipient => this.resolveContactByEmail(recipient.email) || recipient)
            .reduce((acc, recipient) => {
              if (!acc.some(existing => normalizeEmail(existing.email) === normalizeEmail(recipient.email))) {
                acc.push(recipient)
              }
              return acc
            }, [])
          if (replyToEmail) {
            const hasReplyToAlready = this.to.some(recipient => normalizeEmail(recipient.email) === normalizeEmail(replyToEmail))
            if (!hasReplyToAlready) {
              this.to.push({ email: replyToEmail, name: '' })
            }
          }
        }
      } else if (this.sendMailData?.emailToReply?.replyTo) {
        this.to = [{ email: this.sendMailData.emailToReply.replyTo, name: '' }]
      } else if (this.sendMailData.contacts.length > 0) {
        this.to = this.sendMailData.contacts.reduce((list, contact) => {
          if (!(contact.active ?? true)) {
            return list
          }
          const entries = this.getSendableEmailEntries(contact).filter(entry => this.from?.outboundEmail !== entry.email)
          list.push(...entries)
          return list
        }, [])
      } else if (this.sendMailData.preselectedContact) {
        const preselected = this.sendMailData.preselectedContact
        if (Array.isArray(preselected)) {
          this.to = preselected
            .filter(contact => (contact.active ?? true))
            .flatMap(contact => this.getSendableEmailEntries(contact))
        } else {
          this.to = (preselected.active ?? true) ? this.getSendableEmailEntries(preselected) : []
        }
      }

      // Handle forwarding case
      if (this.sendMailData?.emailToReply?.isForwarding) {
        this.to = []
      }

      if (this.sendMailData.cc.length > 0) {
        this.cc = this.sendMailData.cc
          .filter(cc => cc.email !== this.from?.outboundEmail && (cc.active ?? true))
          .map(cc => this.contacts.find(c => c.email === cc.email) || cc)
      }

      if (this.sendMailData.subject) {
        this.subject = this.sendMailData.subject
      }
      if (this.sendMailData.additionalText) {
        this.bodyHtml = `<div><br />${this.sendMailData.additionalText}</div>${this.signatureForCurrentUser}`
      }

      // Reset the changed modal state after initial setup
      this.$store.dispatch('user/setChangedModal', false).catch(e => console.log(e))
    },
    getWeekday(date) {
      const day = new Date(date * 1000)
      let locale = 'en-EN'
      switch (this.$store.getters['internationalization/getCurrentLanguage']) {
        case 'de':
          locale = 'de-DE'
          break
        case 'it':
          locale = 'it-IT'
          break
      }
      return day.toLocaleDateString(locale, { weekday: 'long' })
    },
    restoreDraftReplyContext() {
      if (!this.sendMailData?.isDraft) {
        return
      }
      if (this.sendMailData.forwardingMailId) {
        this.isForward = true
        this.isReply = false
        this.replyToMailId = this.sendMailData.forwardingMailId
        return
      }
      if (this.sendMailData.replyToMailId) {
        this.isReply = true
        this.isForward = false
        this.replyToMailId = this.sendMailData.replyToMailId
        return
      }
      this.isReply = false
      this.isForward = false
      this.replyToMailId = ''
    },
    async prepareMailWithReply() {
      // Snapshot before awaits — store emailToReply can be cleared on modal hide (COOZ-15720)
      const emailToReply = this.sendMailData.emailToReply
      if (!emailToReply) {
        return
      }
      let to = ''
      let cc = ''
      emailToReply.to?.forEach(element => {
        to = to + `${element.name}&lt;${element.email}&gt; `
      })
      if (emailToReply.cc?.length > 0) {
        emailToReply.cc.forEach(element => {
          cc = cc + `${element.name}&lt;${element.email}&gt; `
        })
        cc = `<br><b>CC</b>: ${cc}`
      }
      this.subject = 'Re: ' + emailToReply.subject

      const bodyHtml = this.protectAngleBracketedEmails(
        await this.replaceCidImage(emailToReply)
      )

      // Modal may have been closed while fetching full body / CID images
      if (!this.sendMailData.emailToReply || !this.$store.getters['user/isSendMailModalOpen']) {
        return
      }

      if (!this.sendMailData.isDraft && this.bodyHtml) {
        this.bodyHtml = `
            <div><br /></div>
            ${this.buildSignatureSection(this.getSignatureForUserId(this.userId, this.from?.signatureId))}<br />
            <div class="email-content">
              <br><b>${this.$t('message.marketingMessages.mail.from')}</b>: ${emailToReply.from?.name}&lt;${emailToReply.from?.email}&gt;
              <br><b>${this.$t('message.marketingMessages.mail.sent')}</b>: ${this.getWeekday(emailToReply.timestamp)}, ${this.$options.filters.timestampToDateTime(emailToReply.timestamp)}
              <br><b>${this.$t('message.marketingMessages.mail.to')}</b>: ${to}${cc}
              <br><b>${this.$t('message.marketingMessages.mail.subject')}</b>: ${emailToReply.subject}<br />
              <br>${bodyHtml}
            </div>`
      }
      this.replyToMailId = emailToReply.objectID || emailToReply.id || ''
      if (emailToReply.attachmentMediaIds && emailToReply.attachmentMediaIds.length > 0 && emailToReply.hasForwarding) {
        // Filter out CID-mapped images from attachments (inline images should not appear as attachments)
        const cidMappedMediaIds = new Set(
          (emailToReply.cidMappings || []).map(mapping => mapping.mediaId)
        )
        const filteredAttachmentIds = emailToReply.attachmentMediaIds.filter(
          mediaId => !cidMappedMediaIds.has(mediaId)
        )

        if (filteredAttachmentIds.length > 0) {
          const promises = filteredAttachmentIds.map(media => MediaApi.getMediaByID(media))

          Promise.all(promises)
            .then((response) => {
              this.mediasList = response.map(obj => obj.media)
              this.medias = response.map(obj => obj.media.id)
              this.syncAttachmentTotalBytes()
            })
        } else {
          // All attachments were CID-mapped inline images, clear the attachment list
          this.mediasList = []
          this.medias = []
          this.syncAttachmentTotalBytes()
        }
      }
    },
    async prepareMailToForward() {
      // Snapshot before awaits — store emailToForward can be cleared on modal hide (COOZ-15720)
      const emailToForward = this.sendMailData.emailToForward
      if (!emailToForward) {
        return
      }
      let to = ''
      let cc = ''
      emailToForward.to?.forEach(element => {
        to = to + `${element.name}&lt;${element.email}&gt; `
      })
      if (emailToForward.cc?.length > 0) {
        emailToForward.cc.forEach(element => {
          cc = cc + `${element.name}&lt;${element.email}&gt; `
        })
        cc = `<br><b>CC</b>: ${cc}`
      }
      this.subject = 'Fwd: ' + emailToForward.subject

      const bodyHtml = this.protectAngleBracketedEmails(
        await this.replaceCidImage(emailToForward)
      )

      // Modal may have been closed while fetching full body / CID images
      if (!this.sendMailData.emailToForward || !this.$store.getters['user/isSendMailModalOpen']) {
        return
      }

      if (!this.sendMailData.isDraft && this.bodyHtml) {
        this.bodyHtml = `
            <div><br /></div>
            ${this.buildSignatureSection(this.getSignatureForUserId(this.userId, this.from?.signatureId))}<br />
            <div class="email-content">
              <br><b>${this.$t('message.marketingMessages.mail.from')}</b>: ${emailToForward.from?.name}&lt;${emailToForward.from?.email}&gt;
              <br><b>${this.$t('message.marketingMessages.mail.sent')}</b>: ${this.getWeekday(emailToForward.timestamp)}, ${this.$options.filters.timestampToDateTime(emailToForward.timestamp)}
              <br><b>${this.$t('message.marketingMessages.mail.to')}</b>: ${to}${cc}
              <br><b>${this.$t('message.marketingMessages.mail.subject')}</b>: ${emailToForward.subject}<br />
              <br>${bodyHtml}
            </div>`
      }

      this.replyToMailId = emailToForward.objectID || emailToForward.id || ''
      if (emailToForward.attachmentMediaIds?.length && emailToForward.hasForwarding) {
        // Filter out CID-mapped images from attachments (inline images should not appear as attachments)
        const cidMappedMediaIds = new Set(
          (emailToForward.cidMappings || []).map(mapping => mapping.mediaId)
        )
        const filteredAttachmentIds = emailToForward.attachmentMediaIds.filter(
          mediaId => !cidMappedMediaIds.has(mediaId)
        )

        if (filteredAttachmentIds.length > 0) {
          const promises = filteredAttachmentIds.map(media => MediaApi.getMediaByID(media))

          Promise.all(promises)
            .then((response) => {
              this.mediasList = response.map(obj => obj.media)
              this.medias = response.map(obj => obj.media.id)
              this.syncAttachmentTotalBytes()
            })
        } else {
          // All attachments were CID-mapped inline images, clear the attachment list
          this.mediasList = []
          this.medias = []
          this.syncAttachmentTotalBytes()
        }
      }
    },
    emailNeedsFullBodyForQuote(email) {
      if (!email) {
        return false
      }
      if (email.missingHtml) {
        return true
      }
      const bodyHtml = email.bodyHtml
      if (!bodyHtml?.trim()) {
        return true
      }
      return !/<(?:table|img|div|span|p|section)\b/i.test(bodyHtml)
    },
    async resolveEmailForQuote(email) {
      const mailId = email?.id || email?.objectID
      if (!mailId || !this.emailNeedsFullBodyForQuote(email)) {
        return email
      }
      try {
        const response = await MessageApi.listMailsByIds([mailId])
        const full = response.mails?.[0]
        if (!full) {
          return email
        }

        const fullHtml = full.bodyHtml?.trim() || ''
        const currentHtml = email.bodyHtml?.trim() || ''

        // listMailsByIds can return empty bodyHtml while list/detail still have
        // usable synthesized HTML or bodyText — never wipe the quote with empty HTML.
        if (!fullHtml) {
          if (currentHtml) {
            return {
              ...full,
              bodyHtml: email.bodyHtml,
              bodyText: email.bodyText || full.bodyText
            }
          }
          const text = full.bodyText || email.bodyText
          if (text) {
            return {
              ...full,
              bodyHtml: this.protectAngleBracketedEmails(
                '<br><br>' + this.escapeHtml(this.decodeHtmlEntitiesIfNeeded(String(text))).replaceAll('\n', '<br>')
              )
            }
          }
          return email
        }

        if (email.missingHtml || fullHtml.length >= currentHtml.length) {
          return full
        }
        return email
      } catch (e) {
        console.error('Error loading full email for quote:', e)
      }
      return email
    },
    async replaceCidImage(email) {
      email = await this.resolveEmailForQuote(email)
      let bodyHtml = email.bodyHtml
      if (!bodyHtml?.trim() && email.bodyText) {
        bodyHtml = this.protectAngleBracketedEmails(
          '<br><br>' + this.escapeHtml(this.decodeHtmlEntitiesIfNeeded(String(email.bodyText))).replaceAll('\n', '<br>')
        )
      }
      if (email.cidMappings && email.cidMappings.length > 0) {
        // Load CID-mapped images separately (they are not in mediasList as they are inline images)
        const cidMediaIds = email.cidMappings.map(mapping => mapping.mediaId)
        let cidMediaMap = new Map()

        if (cidMediaIds.length > 0) {
          try {
            const promises = cidMediaIds.map(mediaId => MediaApi.getMediaByID(mediaId))
            const responses = await Promise.all(promises)
            responses.forEach(response => {
              if (response.media) {
                cidMediaMap.set(response.media.id, response.media)
              }
            })
          } catch (e) {
            console.error('Error loading CID images:', e)
          }
        }

        return bodyHtml?.replace(
          /<img([^>]*?)src="cid:([^"]*?)"(.*?)>/gi,
          (match, pre, cid, post) => {
            const matchingCid = email.cidMappings.find(image => image.cid === cid)

            if (!matchingCid) return match
            const matchingImage = cidMediaMap.get(matchingCid.mediaId)
            if (!matchingImage) return match

            const hasExistingSrc = /src="[^"]*"/.test(match)
            if (hasExistingSrc) {
              return match.replace(/src="[^"]*"/, `src="${matchingImage.url}"`)
            }

            return `<img${pre}src="${matchingImage.url}"${post}>`
          }
        ) || bodyHtml || ''
      } else {
        return bodyHtml?.replace(
          /<img([^>]*?)data-filename="([^"]*?)"(.*?)>/gi,
          (match, pre, dataFilename, post) => {
            const matchingImage = this.mediasList.find(image => image.filename === dataFilename)

            if (!matchingImage) return match

            const hasExistingSrc = /src="[^"]*"/.test(match)
            if (hasExistingSrc) {
              return match.replace(/src="[^"]*"/, `src="${matchingImage.url}"`)
            }

            return `<img${pre}data-filename="${dataFilename}" src="${matchingImage.url}"${post}>`
          }
        ) || bodyHtml || ''
      }
    },
    hide() {
      setTimeout(() => {
        this.$refs['modal-send-mail'].hide()
      }, 100)
    },
    loadTemplate(objectId) {
      let contactId = ''
      if (this.to && this.to.length > 0) {
        // Find contact to render the template with
        contactId = this.to[0].id || this.to[0].objectID
        if (!contactId) {
          const contact = this.sendMailData.contacts.find(c => c.email === this.to[0].email)
          if (contact) {
            contactId = contact.id || contact.objectID
          }
        }
      }
      let currentObjectId = ''
      if (this.to?.length === 1 && this.contactsWithObject.length > 0) {
        currentObjectId = this.contactsWithObject.find(c => c.contacts.some(contactId => contactId === this.to[0].id || contactId === this.to[0].objectID))?.objectId
      } else {
        currentObjectId = objectId === '' && this.renderWithObjectId !== '' && this.contactsWithObject.length === 1 ? this.renderWithObjectId : this.renderWithObjectId !== '' ? this.renderWithObjectId : objectId !== '' ? objectId : ''
      }
      MessageApi.renderTemplate(this.templateId.id, this.to.length === 1 && contactId !== '' ? contactId : '', currentObjectId)
        .then(response => {
          this.bodyHtml = '<div>' + response.text + '</div>' + (this.sendMailData.additionalText ? `\n\n${this.sendMailData.additionalText}\n\n` : '\n') + this.signatureForCurrentUser + (this.sendMailData.emailToReply ? `\n\n${this.$t('message.marketingMessages.history')}\n${this.sendMailData.emailToReply.bodyHtml}` : '')
          this.subject = response.subject
          if (response.attachmentMediaIds.length > 0) {
            const promises = response.attachmentMediaIds.map(mediaId => MediaApi.getMediaByID(mediaId))

            Promise.all(promises)
              .then((response) => {
                this.mediasList = response.map(obj => obj.media)
                this.medias = response.map(obj => obj.media.id)
                this.syncAttachmentTotalBytes()
              })
              .catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
              })
          }
        })
    },
    async applyCSSToAllElements() {
      const editor = this.$refs['editor-tiny']?.editor

      if (!editor) {
        return this.bodyHtml
      }

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = JSON.parse(JSON.stringify(this.bodyHtml))

      const editorBody = editor.getBody()
      if (!editorBody) {
        return tempDiv.innerHTML
      }

      const bodyComputedStyle = window.getComputedStyle(editorBody)

      // Default style from the editor body (user's font preferences)
      const defaultStyle = {
        fontSize: bodyComputedStyle.fontSize,
        fontFamily: bodyComputedStyle.fontFamily,
        color: bodyComputedStyle.color
      }

      // Build effective style for an element by combining its own inline styles
      // with the inherited parent style (simulates CSS inheritance for detached DOM)
      const getEffectiveStyle = (element, parentEffectiveStyle) => {
        return {
          fontSize: element.style?.fontSize || parentEffectiveStyle.fontSize,
          fontFamily: element.style?.fontFamily || element.getAttribute?.('face') || parentEffectiveStyle.fontFamily,
          color: element.style?.color || parentEffectiveStyle.color
        }
      }

      // Recursively traverse elements and apply inline styles where missing
      // Properly follows CSS inheritance: child elements inherit from their parent's
      // effective style, preserving the original email's font hierarchy
      const processElement = (element, parentEffectiveStyle = defaultStyle) => {
        // Resolve this element's effective style (its own inline styles + inherited from parent)
        const effectiveStyle = getEffectiveStyle(element, parentEffectiveStyle)

        const children = element.children

        for (let child of children) {
          if (child.tagName.toLowerCase() === 'br') {
            continue
          }

          // Check for explicit inline styles on the child element
          // Elements with explicit styles (from original emails, user formatting, etc.) are preserved
          const hasExplicitFontSize = child.style.fontSize || child.getAttribute('size')
          const hasExplicitFontFamily = child.style.fontFamily || child.getAttribute('face')
          const hasExplicitColor = child.style.color

          let stylesToApply = ''

          if (!hasExplicitFontSize) {
            stylesToApply += `font-size: ${effectiveStyle.fontSize}; `
          }
          if (!hasExplicitFontFamily) {
            stylesToApply += `font-family: ${effectiveStyle.fontFamily}; `
          }
          if (!hasExplicitColor) {
            stylesToApply += `color: ${effectiveStyle.color}; `
          }

          if (stylesToApply) {
            const existingStyle = child.getAttribute('style') || ''
            child.style.cssText = `${existingStyle}; ${stylesToApply} margin: 0;`
          }

          // Continue processing children with the child's effective style
          // This ensures proper inheritance (e.g., a child inside a Calibri 11pt div
          // will inherit Calibri 11pt, not the editor body's default font)
          processElement(child, getEffectiveStyle(child, effectiveStyle))
        }
      }

      // Process all elements recursively (applies inline styles to all content)
      processElement(tempDiv)

      return tempDiv.innerHTML
    },
    async sendMail(asDraft = false) {
      this.syncAttachmentTotalBytes()
      if (exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, this.outgoingEmailMailboxType)) {
        this.showAttachmentSizeError()
        this.loading = false
        return
      }
      if (!asDraft && !this.validateAllRecipientsForSend()) {
        this.loading = false
        return
      }
      this.lastMailboxId = this.from.id
      if (asDraft) {
        MessageApi.saveDraft(
          this.convertToPlainWithBreakingLines(this.bodyHtml),
          this.bodyHtml,
          {
            email: this.from.outboundEmail,
            name: this.from.name
          },
          this.subject,
          this.to.map((to) => {
            return {
              email: this.recipientEmailForSend(to),
              name: to.name
            }
          }),
          this.isDraft ? this.draftId : null,
          this.to.filter(to => to.id || to.objectID).map(to => to.id || to.objectID)
            .concat(this.cc.filter(cc => cc.id || cc.objectID).map(cc => cc.id || cc.objectID))
            .concat(this.sendMailData.referenceIds)
            .concat(this.sendMailData.emailToReply ? this.sendMailData.emailToReply.referenceIds.filter(e => e && !e.startsWith('contact_')) : [])
            .concat(this.from.contactId)
            .filter(Boolean),
          this.medias,
          this.from.id,
          new Date().getTime(),
          this.cc.map((cc) => {
            return {
              email: this.recipientEmailForSend(cc),
              name: cc.name
            }
          }),
          this.bcc.map((bcc) => {
            return {
              email: this.recipientEmailForSend(bcc),
              name: bcc.name
            }
          }),
          this.isForward ? '' : (this.replyToMailId || this.sendMailData?.replyToMailId || ''),
          this.isForward ? (this.replyToMailId || this.sendMailData?.forwardingMailId || '') : ''
        ).then(response => {
          Vue.toasted.show(this.$t('message.successMessages.mailSavedDraft'), { type: 'success' })
          this.$v.$reset()
          this.addSentMail(response.mail)
          this.hide()
          this.closeAll()
          this.$parent.$refs['coozzy-header'].countDraftMails(true)
          if (this.sendMailData.contactsWithoutEmail.length > 0) {
            setTimeout(() => {
              this.$bvModal.show('listContactsWithoutEmailModal')
            }, 200)
          }
        }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.mailSavedDraft'), { type: 'error' })
        }).finally(() => {
          this.loading = false
        })
      } else {
        const bodyWithCss = await this.applyCSSToAllElements()
        try {
          if (!this.sendMailData.toAllTo && (this.sendMailData.useBcc || this.sendMailData.contacts.length > 1 || this.bodyHtml.search('#anrede#') !== -1 || this.subject.search('#anrede#') !== -1 || this.bodyHtml.search('#obj') !== -1 || this.subject.search('#obj') !== -1)) {
            let localContactsWithObject = [...this.contactsWithObject]
            // Get all new email addresses from to/cc/bcc that aren't in contacts yet
            const allRecipients = [...this.to, ...this.cc, ...this.bcc]
            const newEmailContacts = allRecipients.filter(recipient => {
              return !localContactsWithObject.some(cwo =>
                cwo.contacts.some(contactId => {
                  const contact = this.contacts.find(c => c.id === contactId)
                  return contact && (contact.email === recipient.email || contact.workEmail?.email === recipient.email)
                })
              )
            })

            if (newEmailContacts.length > 0) {
              const newContactsWithObject = newEmailContacts
                .filter(contact => contact.id || contact.objectID)
                .map(contact => ({
                  contacts: [contact.id || contact.objectID],
                  objectId: this.sendMailData.renderWithObjectId || ''
                }))
              localContactsWithObject = [...localContactsWithObject, ...newContactsWithObject]
            }

            const uniqueTo = this.getUniqueListByID(this.to, true)
            const textEntries = uniqueTo.flatMap(contact => {
              const contactId = contact.id || contact.objectID

              if (localContactsWithObject.length > 0) {
                // Find all matching objects for this contact
                const matchingEntries = localContactsWithObject
                  .filter(obj => obj.contacts.includes(contact.id))
                  .map(obj => ({
                    contactId: contact.id,
                    objectId: obj.objectId
                  }))

                // If matches found return them, otherwise return default entry
                return matchingEntries.length > 0
                  ? matchingEntries
                  : [{
                    contactId,
                    objectId: this.sendMailData.renderWithObjectId || ''
                  }]
              }

              // Default entry when no objects to match against
              return [{
                contactId,
                objectId: this.sendMailData.renderWithObjectId || ''
              }]
            })

            // Replace images with placeholders to avoid rendering issues
            const { processedHtml, imageMap } = this.extractAndReplaceImages(bodyWithCss)
            const renderTextResponse = await MessageApi.bulkRenderText(textEntries, processedHtml, this.subject)
            renderTextResponse.entries = renderTextResponse.entries.map(entry => ({
              ...entry,
              text: this.restoreImages(entry.text, imageMap)
            }))

            const fromContact = await ContactApi.resolveByMail(this.accountId, [this.from.outboundEmail])
            if ((fromContact.companies.concat(fromContact.persons)).length === 1) {
              this.from.contactId = (fromContact.companies.concat(fromContact.persons))[0]?.id
            }
            const entries = []
            for (let index = 0; index < uniqueTo.length; index++) {
              let el = []
              if (uniqueTo[index].id || uniqueTo[index].objectID) {
                el = renderTextResponse.entries.filter(x => x.contactId === uniqueTo[index].id || x.contactId === uniqueTo[index].objectID)
              } else {
                el = renderTextResponse.entries.filter(x => x.contactId === '')
              }
              const contactId = uniqueTo[index].id || uniqueTo[index].objectID
              const bccRefs = this.sendMailData.bccReferenceIdsByContacts?.[contactId] || []
              const referenceIds = bccRefs.concat(this.sendMailData.referenceIds || [])
              const bodyContainsObjPlaceholder = (bodyWithCss.search('#obj') !== -1)
              let text = ''
              if (el.length > 0) {
                if (bodyContainsObjPlaceholder) {
                  el.forEach(element => {
                    text = text + element.text + '<br>'
                  })
                } else {
                  text = text + el[0].text + '<br>'
                }
              }
              entries.push({
                bodyText: this.convertToPlainWithBreakingLines(text || this.bodyHtml),
                bodyHtml: text || bodyWithCss,
                from: {
                  email: this.from.outboundEmail,
                  name: this.from.name
                },
                subject: el?.length > 0 ? el[0]?.title : this.subject,
                to: [{
                  email: this.recipientEmailForSend(uniqueTo[index]),
                  name: uniqueTo[index].name
                }],
                ownerId: this.accountId,
                referenceIds: ([uniqueTo[index].id || uniqueTo[index].objectID].concat(referenceIds).concat(this.cc.map((cc) => cc.id || cc.objectID)).concat(this.bcc.map((bcc) => bcc.id || bcc.objectID)).concat(this.from.contactId)).filter(x => x),
                attachmentMediaIds: this.medias,
                replyToMailId: this.replyToMailId,
                cc: this.cc.map((cc) => {
                  return {
                    email: this.recipientEmailForSend(cc),
                    name: cc.name
                  }
                }),
                bcc: this.bcc.map((bcc) => {
                  return {
                    email: this.recipientEmailForSend(bcc),
                    name: bcc.name
                  }
                }),
                confidential: this.confidentialEmail
              })
            }
            let loopVar = entries.length
            // Send emails in groups of up to 4 recipients when not using SPF mailboxes
            if (this.from.mailboxType !== 'MAILBOX_TYPE_SPF') {
              loopVar = entries.length > 4 ? 4 : entries.length
            } else {
              // Use 10 recipients per request (20 if over 100) when using SPF mailboxes
              const bulkGroupSize = entries.length > 100 ? 20 : 10
              loopVar = entries.length > bulkGroupSize ? bulkGroupSize : entries.length
            }
            loopVar = Math.max(1, loopVar)
            let start = 0
            let end = 0
            const mails = []
            let hasErrors = false
            const totalBatches = Math.ceil(entries.length / loopVar)
            // Send emails sequentially, one group at a time
            for (let index = 0; index < totalBatches; index++) {
              start = end
              end = Math.min(entries.length, start + loopVar)
              if (start < entries.length) {
                try {
                  const result = await MessageApi.bulkSendMail(entries.slice(start, end))
                  mails.push(result)
                } catch (e) {
                  console.log(e)
                  hasErrors = true
                  Vue.toasted.show(this.$t('message.savingErrors.mailSend'), { type: 'error' })
                  break
                }
              }
            }
            const allBatchesSucceeded = !hasErrors && mails.length === totalBatches
            if (allBatchesSucceeded) {
              if (this.sendMailData.isDraft) {
                await this.deleteDrafts([this.sendMailData.id])
              }
              Vue.toasted.show(this.$t('message.successMessages.mailSend'), { type: 'success' })
              this.$v.$reset()
              const mailsList = [...new Set([].concat(...mails.map((o) => o.mail)))]
              mailsList.forEach(element => {
                this.addSentMail(element)
              })
              await this.saveTenantChangeNotificationPdf(bodyWithCss)
              this.hide()
              if (this.sendMailData.contactsWithoutEmail.length > 0) {
                setTimeout(() => {
                  this.$bvModal.show('listContactsWithoutEmailModal')
                }, 200)
              }
            }
          } else {
            const fromContact = await ContactApi.resolveByMail(this.accountId, [this.from.outboundEmail])
            if ((fromContact.companies.concat(fromContact.persons)).length === 1) {
              this.from.contactId = (fromContact.companies.concat(fromContact.persons))[0]?.id
            }

            const response = await MessageApi.sendMail(
              this.convertToPlainWithBreakingLines(this.bodyHtml),
              bodyWithCss,
              {
                email: this.from.outboundEmail,
                name: this.from.name
              },
              this.subject,
              this.getUniqueListByID(this.to, true).map((to) => {
                return {
                  email: this.recipientEmailForSend(to),
                  name: to.name
                }
              }),
              this.getUniqueListByID(this.to).filter(to => to.id || to.objectID).map(to => to.id || to.objectID)
                .concat(this.cc.filter(cc => cc.id || cc.objectID).map(cc => cc.id || cc.objectID))
                .concat(this.sendMailData.referenceIds)
                .concat(this.sendMailData.emailToReply ? this.sendMailData.emailToReply.referenceIds.filter(e => e && !e.startsWith('contact_')) : [])
                .concat(this.from.contactId)
                .filter(Boolean),
              this.medias,
              this.accountId,
              this.replyToMailId,
              this.cc.map((cc) => {
                return {
                  email: this.recipientEmailForSend(cc),
                  name: cc.name
                }
              }),
              this.bcc.map((bcc) => {
                return {
                  email: this.recipientEmailForSend(bcc),
                  name: bcc.name
                }
              }),
              this.confidentialEmail
            )

            if (this.sendMailData.isDraft) {
              await this.deleteDrafts([this.sendMailData.id])
            }
            Vue.toasted.show(this.$t('message.successMessages.mailSend'), { type: 'success' })
            this.$v.$reset()
            this.addSentMail(response.mail)
            await this.saveTenantChangeNotificationPdf(bodyWithCss)
            this.hide()
            if (this.sendMailData.contactsWithoutEmail.length > 0) {
              setTimeout(() => {
                this.$bvModal.show('listContactsWithoutEmailModal')
              }, 200)
            }
          }
        } catch (e) {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.mailSend'), { type: 'error' })
        } finally {
          this.loading = false
        }
      }
    },
    async saveTenantChangeNotificationPdf(bodyWithCss) {
      const ctx = this.sendMailData.tenantChangeNotification
      if (!ctx) {
        return
      }

      const targets = []
      if (ctx.movingInTenancyId) {
        targets.push({
          tenancyId: ctx.movingInTenancyId,
          ownerId: ctx.movingInOwnerId
        })
      }
      if (ctx.movingOutTenancyId) {
        targets.push({
          tenancyId: ctx.movingOutTenancyId,
          ownerId: ctx.movingOutOwnerId
        })
      }
      if (targets.length === 0) {
        return
      }

      try {
        const printableHtml = buildPrintableOutboundEmailHtml({
          from: {
            name: this.from.name,
            email: this.from.outboundEmail
          },
          to: this.getUniqueListByID(this.to, true).map((recipient) => ({
            name: recipient.name,
            email: this.recipientEmailForSend(recipient)
          })),
          cc: this.cc.map((recipient) => ({
            name: recipient.name,
            email: this.recipientEmailForSend(recipient)
          })),
          bcc: this.bcc.map((recipient) => ({
            name: recipient.name,
            email: this.recipientEmailForSend(recipient)
          })),
          subject: this.subject,
          bodyHtml: bodyWithCss,
          includeHeader: false,
          labels: {
            from: this.$t('message.marketingMessages.mail.from'),
            to: this.$t('message.marketingMessages.mail.to'),
            cc: 'CC',
            bcc: 'BCC',
            subject: this.$t('message.marketingMessages.mail.subject')
          }
        })

        const pdfBlob = await renderEmailPdfBlob(printableHtml)
        const documentName = this.$t('message.onBoarding.buildings.objects.notificationChangeTenant')
        const fileName = `${documentName}.pdf`
        const documentsByOwner = new Map()
        const storeEntries = []

        for (const target of targets) {
          if (!target.tenancyId || !target.ownerId) {
            continue
          }

          let document = documentsByOwner.get(target.ownerId)
          if (!document) {
            const formData = new FormData()
            formData.append('file', pdfBlob, fileName)
            formData.append('filename', fileName)
            formData.append('public', 'false')
            formData.append('accountId', target.ownerId.toString())

            const media = await MediaApi.uploadMedia(formData)
            document = await MediaApi.createDocument(
              target.ownerId,
              documentName,
              'OTHER',
              media.id,
              false
            )
            documentsByOwner.set(target.ownerId, document)
          }

          await ObjectApi.addTenancyDocument(target.tenancyId, [document.id])
          storeEntries.push({
            tenancyId: target.tenancyId,
            document
          })
        }

        if (storeEntries.length > 0) {
          this.$store.commit('onboarding/addTenancyRentalDocuments', { entries: storeEntries })
          eventBus.$emit('tenant-change-rental-documents-added', { entries: storeEntries })
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.savingErrors.file'), { type: 'error' })
      }
    },
    getUniqueListByID(arr, checkEmail = false) {
      const withId = arr.filter(item => item.id)
      const withoutId = arr.filter(item => !item.id)
      const uniqueWithId = Array.from(
        withId.reduce((map, item) => {
          const key = checkEmail ? `${item.id}-${item.email}` : item.id
          if (!map.has(key)) {
            map.set(key, item)
          }
          return map
        }, new Map()).values()
      )

      return [...uniqueWithId, ...withoutId]
    },
    async forwardMail() {
      this.syncAttachmentTotalBytes()
      if (exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, this.outgoingEmailMailboxType)) {
        this.showAttachmentSizeError()
        this.loading = false
        return
      }
      if (!this.validateAllRecipientsForSend()) {
        this.loading = false
        return
      }
      try {
        const bodyWithCss = await this.applyCSSToAllElements()
        this.lastMailboxId = this.from.id

        if (this.sendMailData.useBcc || this.bodyHtml.search('#anrede#') !== -1 || this.subject.search('#anrede#') !== -1 || this.bodyHtml.search('#obj') !== -1 || this.subject.search('#obj') !== -1) {
          const promises = []
          for (let index = 0; index < this.to.length; index++) {
            // Find contact to render the template with
            let contactId = ''
            contactId = this.to[index].id || this.to[index].objectID
            if (!contactId) {
              const contact = this.sendMailData.contacts.find(c => c.email === this.to[0].email)
              if (contact) {
                contactId = contact.id || contact.objectID
              }
            }

            let bodyHtmlUpdated = bodyWithCss
            let titleUpdated = this.subject
            if (this.contactsWithObject.length > 1) {
              for (let j = 0; j < this.contactsWithObject.length; j++) {
                if (this.contactsWithObject[j].contacts.find(x => x === this.to[index].id)) {
                  const renderTextResponse = await MessageApi.renderText(this.to[index].id, this.contactsWithObject[j].objectId, bodyWithCss, this.subject)
                  bodyHtmlUpdated = renderTextResponse.text
                  titleUpdated = renderTextResponse.title
                }
              }
            } else {
              const renderTextResponse = await MessageApi.renderText(this.to[index].id || this.to[index].objectID, '', bodyWithCss, this.subject)
              bodyHtmlUpdated = renderTextResponse.text
              titleUpdated = renderTextResponse.title
            }

            const referenceIds = (() => {
              const rawReferenceIds = [
                this.to[index].id || this.to[index].objectID,
                ...(this.sendMailData.bccReferenceIdsByContacts[this.to[index].id || this.to[index].objectID] || []),
                ...this.cc.map(cc => cc.objectID),
                ...this.bcc.map(bcc => bcc.objectID)
              ]

              // Log detected corrupted data
              if (rawReferenceIds.some(id => id === null || id === undefined)) {
                Sentry.setExtra('replyToMailId', this.replyToMailId)
                Sentry.setExtra('toContactId', this.to[index].id)
                Sentry.setExtra('toContactObjectID', this.to[index].objectID)
                Sentry.setExtra('bccReferenceIdsByContacts', JSON.stringify(this.sendMailData.bccReferenceIdsByContacts[this.to[index].id || this.to[index].objectID] || []))
                Sentry.setExtra('ccObjectIDs', JSON.stringify(this.cc.map(cc => cc.objectID)))
                Sentry.setExtra('bccObjectIDs', JSON.stringify(this.bcc.map(bcc => bcc.objectID)))
                Sentry.captureMessage('Corrupted data detected in forward mail referenceIds')
              }

              return [...new Set(rawReferenceIds)].filter(Boolean)
            })()

            promises.push(MessageApi.forwardMail(
              this.convertToPlainWithBreakingLines(bodyHtmlUpdated),
              bodyHtmlUpdated,
              {
                email: this.from.outboundEmail,
                name: this.from.name
              },
              titleUpdated,
              [{
                email: this.recipientEmailForSend(this.to[index]),
                name: this.to[index].name
              }],
              referenceIds,
              this.medias,
              this.accountId,
              this.replyToMailId,
              this.cc.map((cc) => {
                return {
                  email: this.recipientEmailForSend(cc),
                  name: cc.name
                }
              }),
              this.bcc.map((bcc) => {
                return {
                  email: this.recipientEmailForSend(bcc),
                  name: bcc.name
                }
              }),
              this.confidentialEmail
            ))
          }

          const mails = await Promise.all(promises)
          Vue.toasted.show(this.$t('message.successMessages.mailSend'), { type: 'success' })
          if (this.sendMailData.isDraft) {
            await this.deleteDrafts([this.sendMailData.id])
          }
          this.$v.$reset()
          mails.forEach(element => {
            this.addSentMail(element.mail)
          })
          this.hide()
          if (this.sendMailData.contactsWithoutEmail.length > 0) {
            setTimeout(() => {
              this.$bvModal.show('listContactsWithoutEmailModal')
            }, 200)
          }
        } else {
          const referenceIds = (() => {
            const rawReferenceIds = [
              ...this.to.filter(to => to.id || to.objectID).map(to => to.id || to.objectID),
              ...(this.sendMailData.referenceIds || []),
              ...(this.sendMailData.emailToReply?.referenceIds || [])
            ]

            // Log detected corrupted data
            if (rawReferenceIds.some(id => id === null || id === undefined)) {
              Sentry.setExtra('replyToMailId', this.replyToMailId)
              Sentry.setExtra('toObjectIDs', JSON.stringify(this.to.map(to => to.id || to.objectID)))
              Sentry.setExtra('sendMailDataReferenceIds', JSON.stringify(this.sendMailData.referenceIds || []))
              Sentry.setExtra('emailToReplyReferenceIds', JSON.stringify(this.sendMailData.emailToReply?.referenceIds || []))
              Sentry.captureMessage('Corrupted data detected in forward mail referenceIds')
            }

            return [...new Set(rawReferenceIds)].filter(Boolean)
          })()

          const response = await MessageApi.forwardMail(
            this.convertToPlainWithBreakingLines(this.bodyHtml),
            bodyWithCss,
            {
              email: this.from.outboundEmail,
              name: this.from.name
            },
            this.subject,
            this.to.map((to) => {
              return {
                email: this.recipientEmailForSend(to),
                name: to.name
              }
            }),
            referenceIds,
            this.medias,
            this.accountId,
            this.replyToMailId,
            this.cc.map((cc) => {
              return {
                email: this.recipientEmailForSend(cc),
                name: cc.name
              }
            }),
            this.bcc.map((bcc) => {
              return {
                email: this.recipientEmailForSend(bcc),
                name: bcc.name
              }
            }),
            this.confidentialEmail
          )

          Vue.toasted.show(this.$t('message.successMessages.mailSend'), { type: 'success' })
          if (this.sendMailData.isDraft) {
            await this.deleteDrafts([this.sendMailData.id])
          }
          this.$v.$reset()
          this.addSentMail(response.mail)
          this.hide()
        }
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.savingErrors.mailSend'), { type: 'error' })
      } finally {
        this.loading = false
      }
    },
    async deleteDrafts(ids) {
      try {
        return await MessageApi.deleteDrafts(ids)
      } catch (e) {
        console.log(e)
      }
    },
    toOptions(hits) {
      const excluded = this.bcc.map(obj => obj.email).concat(this.cc.map(obj => obj.email))
      return this.getSortedSuggestions(hits).filter(obj => !excluded.includes(obj.email))
    },
    ccOptions(hits) {
      const excluded = this.to.map(obj => obj.email).concat(this.bcc.map(obj => obj.email))
      return this.getSortedSuggestions(hits).filter(obj => !excluded.includes(obj.email))
    },
    bccOptions(hits) {
      const excluded = this.to.map(obj => obj.email).concat(this.cc.map(obj => obj.email))
      return this.getSortedSuggestions(hits).filter(obj => !excluded.includes(obj.email))
    },
    hitsToSuggestions(hits) {
      const contactPersons = [].concat.apply([], hits.map(x => x.contactPersons ? x.contactPersons : []))
      const list = hits.concat(contactPersons.length > 0 ? contactPersons : [])
      const suggestions = []
      list.forEach(element => {
        this.getSendableEmailEntries(element).forEach(normalized => {
          if (!suggestions.some(s => (s.id || s.objectID) === (normalized.id || normalized.objectID) && s.email === normalized.email)) {
            suggestions.push(normalized)
          }
        })
      })
      return suggestions
    },
    async sendFile() {
      this.uploading = true
      const response = await this.uploadMedia(this.attachment, this.accountId, 'false')
      if (response?.length > 0) {
        for (const media of response) {
          this.mediasList.push(media)
          this.medias.push(media.id)
        }
        this.syncAttachmentTotalBytes()

        this.$nextTick(() => {
          this.attachment = []
          this.uploading = false
          if (this.$refs['coozzy-file-input']) {
            this.$refs['coozzy-file-input'].resetPlaceholder()
          }
        })
      }
      this.$nextTick(() => {
        this.uploading = false
      })
    },
    checkAttachment(asDraft = false) {
      if (asDraft) {
        this.sendMail(asDraft)
      } else {
        this.$v.$touch()
        if (!this.$v.$invalid) {
          if (this.medias.length === 0 && this.hasAttachmentKeywords()) {
            this.$bvModal.show('missing-attachment-warning-modal')
            return
          }
          this.loading = true
          if (this.isForward === true && this.isReply === false) {
            this.forwardMail()
          } else {
            this.sendMail(asDraft)
          }
        }
      }
    },
    extractNewBodyText() {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = this.bodyHtml || ''
      const signature = tempDiv.querySelector('#signature')
      if (signature) signature.remove()
      const emailContent = tempDiv.querySelector('.email-content')
      if (emailContent) emailContent.remove()
      return tempDiv.textContent || ''
    },
    hasAttachmentKeywords() {
      const keywords = [
        'anhang', 'anhänge', 'anhängen', 'angehängt', 'angehangen',
        'beigefügt', 'beigefügte', 'beilage', 'beilagen', 'anbei',
        'attachment', 'attachments', 'attached', 'enclosed', 'enclosure'
      ]
      const bodyText = this.extractNewBodyText().toLowerCase()
      return keywords.some(kw => bodyText.includes(kw))
    },
    sendAnyway() {
      this.$bvModal.hide('missing-attachment-warning-modal')
      this.loading = true
      if (this.isForward === true && this.isReply === false) {
        this.forwardMail()
      } else {
        this.sendMail(false)
      }
    },
    addAttachmentFromWarning() {
      this.$bvModal.hide('missing-attachment-warning-modal')
    },
    deleteMedia(id) {
      this.medias = this.medias.filter(e => e !== id)
      this.mediasList = this.mediasList.filter(el => el.id !== id)
      this.syncAttachmentTotalBytes()
    },
    syncAttachmentTotalBytes() {
      this.totalFilesSize = this.mediasList.reduce((sum, m) => sum + (m.size || 0), 0)
    },
    showAttachmentSizeError() {
      Vue.toasted.show(this.$tc(outgoingEmailAttachmentLimitI18nKey(this.outgoingEmailMailboxType)), { type: 'error' })
    },
    validateEmail(email) {
      return isValidEmailFormat(email)
    },
    validateRecipientsForSend(recipients) {
      for (const recipient of recipients) {
        if (!this.recipientEmailForSend(recipient)) {
          Vue.toasted.show(this.$t('message.loadingErrors.contactHasNoEmail'), { type: 'error' })
          return false
        }
      }
      return true
    },
    validateAllRecipientsForSend() {
      const uniqueTo = this.getUniqueListByID(this.to, true)
      if (uniqueTo.length === 0) {
        return false
      }
      return this.validateRecipientsForSend(uniqueTo) && this.validateRecipientsForSend(this.cc) && this.validateRecipientsForSend(this.bcc)
    },
    syncSendMailModalDockMode() {
      this.setSendMailModalDockMode(this.minus ? 'minus' : this.minimized ? 'minimized' : 'none')
    },
    changeMinimized() {
      if (this.minimized) {
        this.$emit('restore')
      } else {
        this.$emit('minimize')
      }
      this.minimized = !this.minimized
      if (this.minimized || this.minus) {
        document.getElementsByClassName('body')[0].style.overflow = 'auto'
      } else {
        document.getElementsByClassName('body')[0].style.overflow = 'hidden'
      }
    },
    minusEvent() {
      if (this.minimized) {
        this.$emit('restore')
      } else {
        this.$emit('minimize')
      }
      this.minus = !this.minus
      if (this.minimized || this.minus) {
        document.getElementsByClassName('body')[0].style.overflow = 'auto'
      } else {
        document.getElementsByClassName('body')[0].style.overflow = 'hidden'
      }
    },
    /**
     * Non-image files accepted by the mail attachment control (PDF, Office, video, etc.).
     * Dropped images stay on TinyMCE's path for inline upload.
     */
    isSendMailAttachmentFile(file) {
      if (!file || !file.name) {
        return false
      }
      if (file.type && file.type.startsWith('image/')) {
        return false
      }
      if (file.type && file.type.startsWith('video/')) {
        return true
      }
      if (file.type && SEND_MAIL_ATTACHMENT_MIMES.has(file.type)) {
        return true
      }
      const parts = file.name.split('.')
      const ext = parts.length > 1 ? parts.pop().toLowerCase() : ''
      if (ext && SEND_MAIL_ATTACHMENT_EXTS.has(ext)) {
        return true
      }
      return false
    },
    stageFilesForAttachment(files) {
      const listFilesLimitExceeded = []
      if (!files || files.length === 0) {
        return listFilesLimitExceeded
      }
      let runningTotal = this.mediasList.reduce((sum, m) => sum + (m.size || 0), 0)
      for (let index = 0; index < files.length; index++) {
        if (exceedsOutgoingEmailAttachmentLimit(runningTotal, files[index].size, this.outgoingEmailMailboxType)) {
          listFilesLimitExceeded.push(index)
        }
        if (listFilesLimitExceeded.filter(x => x === index).length === 0) {
          this.attachment.push(files[index])
          runningTotal += files[index].size
        }
      }
      this.totalFilesSize = runningTotal
      return listFilesLimitExceeded
    },
    notifyMaxAttachmentSizeExceeded(shouldNotify) {
      if (!shouldNotify) {
        return
      }
      this.showAttachmentSizeError()
      this.$nextTick(() => {
        if (this.$refs['coozzy-file-input']) {
          this.$refs['coozzy-file-input'].resetPlaceholder()
        }
      })
    },
    async insertUploadedImageIntoEditor(editor, file) {
      try {
        const response = await this.uploadMedia(
          [file],
          this.accountId,
          'false',
          null,
          () => {}
        )
        if (!response || !response.length || !response[0] || !response[0].url) {
          return
        }
        const img = editor.dom.create('img', { src: response[0].url, alt: '' })
        editor.insertContent(img.outerHTML)
      } catch (e) {
        console.log(e)
      }
    },
    async addFile(event) {
      let fileLimitExceeded = false
      let listFilesLimitExceeded = []
      if (event && event.dataTransfer && event.dataTransfer.getData('itemId')) {
        MediaApi.getMediaByID(event.dataTransfer.getData('itemId'))
          .then(response => {
            const media = response.media
            if (exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, media.size, this.outgoingEmailMailboxType)) {
              fileLimitExceeded = true
            }
            // this.attachment.push(response.media)
            if (!fileLimitExceeded) {
              this.mediasList.push(media)
              this.medias.push(media.id)
              this.syncAttachmentTotalBytes()
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
          })
      } else {
        const files = await this.getFilesFromEvent(event)
        if (files && files.length > 0) {
          let runningTotal = this.mediasList.reduce((sum, m) => sum + (m.size || 0), 0)
          for (let index = 0; index < files.length; index++) {
            if (exceedsOutgoingEmailAttachmentLimit(runningTotal, files[index].size, this.outgoingEmailMailboxType)) {
              listFilesLimitExceeded.push(index)
            }
            if (listFilesLimitExceeded.filter(x => x === index).length === 0) {
              this.attachment.push(files[index])
              runningTotal += files[index].size
            }
          }
          this.totalFilesSize = runningTotal
        }
      }
      if (this.attachment.length > 0) {
        await this.sendFile()
      }
      this.notifyMaxAttachmentSizeExceeded(fileLimitExceeded || listFilesLimitExceeded.length > 0)
    },
    changeColorsToTags() {
      setTimeout(() => {
        this.showRedInfoBar = false
        if (!this.hasDedicated) {
          const current = this
          let tagElements = []
          if (document.getElementById('to-multiselect')) {
            tagElements = document.getElementById('to-multiselect').querySelectorAll('.multiselect__tag')
          }
          if (tagElements) {
            tagElements.forEach(function (element) {
              const domainPart = element.firstChild.innerHTML.split('@')[1]
              if (domainPart === 't-online.de' || domainPart === 't-monline.de') {
                element.classList.add('red-tag')
                current.showRedInfoBar = true
              }
            })
          }
        } else {
          const redTagElements = document.getElementsByClassName('red-tag') || []
          if (redTagElements.length > 0) {
            redTagElements.forEach(function (element) {
              element.classList.remove('red-tag')
            })
          }
        }
      }, 100)
    },
    getSortedSuggestions(hits) {
      const suggestions = this.hitsToSuggestions(hits)

      if (!this.searchQuery) return suggestions

      return suggestions.sort((a, b) => {
        const aMatch = a.email.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (a.name || '').toLowerCase().includes(this.searchQuery.toLowerCase())
        const bMatch = b.email.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (b.name || '').toLowerCase().includes(this.searchQuery.toLowerCase())

        return bMatch - aMatch
      })
    },
    extractAndReplaceImages(html) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      const images = Array.from(tempDiv.getElementsByTagName('img'))
      const imageMap = new Map()

      for (let i = 0; i < images.length; i++) {
        const img = images[i]
        const placeholder = `__IMAGE_PLACEHOLDER_${i}__`
        imageMap.set(placeholder, img.outerHTML)
        img.outerHTML = placeholder
      }

      return {
        processedHtml: tempDiv.innerHTML,
        imageMap
      }
    },
    restoreImages(html, imageMap) {
      let processedHtml = html
      imageMap.forEach((imageHtml, placeholder) => {
        processedHtml = processedHtml.replace(placeholder, imageHtml)
      })
      return processedHtml
    }
  },
  validations: {
    subject: {
      required
    },
    from: {
      required
    },
    to: {
      required,
      isValidEmail
    },
    cc: {
      isValidEmail
    },
    bcc: {
      isValidEmail
    }
  }
}
</script>
<style lang="scss" scoped>
.media-block {
  margin-right: 0.5%;

  .uploaded-file {
    background-position: center center;
    background-size: cover;
    min-height: 7em;
  }

  .file-block {
    border-style: solid;
    border-bottom-color: rgb(206, 212, 218);
    border-width: 0 0 1px 0;

    a {
      overflow-wrap: break-word;
    }
  }

  .delete-icon {
    position: absolute;
    right: 1rem;
  }

  .delete-icon :deep(svg) {
    vertical-align: text-top;
  }
}

:deep(#modal-send-mail:not(.minimized-block) .modal-dialog) {
  max-width: 1200px !important;
}

:deep(.multiselect .multiselect__tags) {
  font-size: .76562rem;
}

:deep(.multiselect__tags .multiselect__single) {
  font-size: .76562rem;
}

:deep(.multiselect .multiselect__input) {
  font-size: .76562rem;
  top: -1px;
}

.secondary-title {
  display: inline;
  font-size: 0.8rem;
  color: initial;
  font-weight: normal;
}

:deep(.red-tag) {
  background-color: $color-red;

  .multiselect__tag-icon:hover {
    background-color: $color-red;;
  }
}

.red-error-bar {
  color: white;
  background: $color-red;
  border-radius: 5px;
  padding: 5px;
}

.width-only-content {
  padding-right: 2rem;
  vertical-align: middle;
}

.confidential-checkbox {
  padding: 0.25rem 0.5rem;
}

:deep(.minimized-block) {
  z-index: 1;
  width: auto;
  height: auto;

  .modal-dialog {
    height: 40vh !important;
    position: fixed;
    right: 25px !important;
    bottom: 16px !important;
    top: auto !important;
    left: auto !important;
    margin: 0 !important;
    transform: none !important;
    max-width: 600px !important;
  }

  .modal-body {
    max-height: 40vh !important;
    overflow-y: scroll;
  }
}

:deep(.minus-block) {
  z-index: 1;
  width: auto;
  height: auto;

  .modal-header {
    background-color: #eeeeee;
    padding: 0.5rem;
    width: 25vw;

    h5 {
      line-height: 2;
    }
  }

  .modal-dialog {
    bottom: 0;
    margin-bottom: 0 !important;
    max-height: 40vh !important;
    position: fixed;
    right: 25px !important;
    top: auto !important;
    left: auto !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    transform: none !important;
    max-width: 600px !important;
  }

  .modal-body {
    display: none;
  }

  .modal-footer {
    display: none;
  }
}

:deep(#modal-send-mail.minus-block + .modal-backdrop[id^='modal-send-mail']) {
  opacity: 0;
  width: auto;
  height: auto;
}

:deep(#modal-send-mail.minimized-block + .modal-backdrop[id^='modal-send-mail']) {
  opacity: 0;
  width: auto;
  height: auto;
}
@media (max-width: 385px) {
  .modal-footer {
    button {
      font-size: 13px;
    }
  }
}
</style>
